import * as React from 'react'
import { useEffect } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import Column from '../../../@ui/containers/grid/column'
import Row from '../../../@ui/containers/grid/row'
import { PaginatedFlatList } from '../../../@ui'
import { Billing } from '../../../generated/graphql'
import { Text, IconButton } from 'react-native-paper'
import { SortOptions, SortField, SortDirection } from '../../../@ui/buttons'
import { useFocusEffect } from '@react-navigation/native'
import { BillingCard } from './components/billing.card'
import { NextMonthEstimateCard } from './components/next-month-estimate.card'
import {
  useGetBillingsQuery,
  useGetNextMonthCostEstimateQuery,
} from '../../../generated/graphql'
import * as Sentry from '@sentry/react-native'
import { View } from 'react-native'
import { Button } from 'react-native-paper'

export function BillingListScreen({
  navigation,
}: StackScreenProps<AppNavigatorParams, 'BillingList'>) {
  const { t } = useTranslation('billing')
  const [sortField, setSortField] = React.useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>('DESC')

  const { data, fetchMore, refetch, error } = useGetBillingsQuery({
    variables: {
      pagination: {
        offset: 0,
        limit: 10,
        sortBy: sortField === 'createdAt' ? 'createdAt' : undefined,
        sort:
          sortField === 'createdAt'
            ? sortDirection === 'ASC'
              ? 'asc'
              : 'desc'
            : undefined,
      },
    },
  })

  const { data: estimateData, refetch: refetchEstimate } =
    useGetNextMonthCostEstimateQuery({
      fetchPolicy: 'cache-and-network',
    })

  useFocusEffect(
    React.useCallback(() => {
      refetch()
      refetchEstimate()
    }, [refetch, refetchEstimate])
  )

  useEffect(() => {
    if (error) {
      console.error('Error fetching billings:', error)
      Sentry.captureException(error)
    }
  }, [error])

  // Refetch when sort changes (backend sorting)
  useEffect(() => {
    refetch()
  }, [sortField, sortDirection, refetch])

  return (
    <ScreenWrapper style={{ padding: 10 }} withScrollView={false}>
      <Column gap={4} flex={1}>
        <Row
          columnGap={4}
          wrap
          justifyContent="space-between"
          alignItems="center"
        >
          <IconButton
            mode={'outlined'}
            icon="arrow-left"
            onPress={() => navigation.goBack()}
          />
          <Button
            mode="outlined"
            icon="wallet"
            onPress={() => navigation.navigate('PaymentMethods')}
            compact
            style={{ marginRight: 8 }}
          >
            {t('payment-methods')}
          </Button>
          <View style={{ alignItems: 'flex-end', flex: 1 }}>
            <SortOptions
              options={[
                {
                  field: 'createdAt',
                  direction: 'DESC',
                  label: t('sort-by-date-desc'),
                  icon: 'sort-calendar-descending',
                },
                {
                  field: 'createdAt',
                  direction: 'ASC',
                  label: t('sort-by-date-asc'),
                  icon: 'sort-calendar-ascending',
                },
              ]}
              selectedField={sortField}
              selectedDirection={sortDirection}
              onSortChange={(field, direction) => {
                setSortField(field)
                setSortDirection(direction)
              }}
            />
          </View>
        </Row>
        {estimateData?.nextMonthCostEstimate && (
          <NextMonthEstimateCard
            estimate={estimateData.nextMonthCostEstimate}
          />
        )}
        <PaginatedFlatList
          data={data?.billings || []}
          total={data?.billings?.length || 0}
          onLoadMore={async (params: any) => {
            try {
              await fetchMore({
                variables: {
                  pagination: {
                    offset: params.offset || data?.billings?.length || 0,
                    limit: params.limit || 10,
                    sortBy: sortField === 'createdAt' ? 'createdAt' : undefined,
                    sort:
                      sortField === 'createdAt'
                        ? sortDirection === 'ASC'
                          ? 'asc'
                          : 'desc'
                        : undefined,
                  },
                },
              })
            } catch (error) {
              console.error('Error loading more billings:', error)
              Sentry.captureException(error)
            }
          }}
          onRefresh={async () => {
            try {
              await refetch()
            } catch (error) {
              console.error('Error refreshing billings:', error)
              Sentry.captureException(error)
            }
          }}
          renderItem={({ item }: any) => {
            const billing = item as Billing
            return <BillingCard billing={billing} />
          }}
          contentContainerStyle={{ flexGrow: 1 }}
        />
      </Column>
    </ScreenWrapper>
  )
}
